/**
 * Video links as they are stored. A YouTube Studio link
 * (studio.youtube.com/video/<id>/edit) is the owner's editor page: framed
 * for learners it shows Google's "401. That's an error", and only the owner
 * can open it. It is turned into the video's public link (youtu.be/<id>)
 * where it is typed and again when it is saved, so none is ever stored.
 */
const STUDIO = /^(?:https?:\/\/)?studio\.youtube\.com\/video\/([\w-]{6,})/i;
const YOUTUBE_ID = /(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/)|youtu\.be\/|studio\.youtube\.com\/video\/)([\w-]{6,})/i;

/** A video link with any YouTube Studio link turned into the public one; anything else unchanged (trimmed). */
export function normalizeVideoLink<T extends string | null | undefined>(url: T): T {
  if (typeof url !== 'string') return url;
  const trimmed = url.trim();
  const studio = trimmed.match(STUDIO);
  return (studio ? `https://youtu.be/${studio[1]}` : trimmed) as T;
}

/** True for a YouTube Studio (editor) link. */
export const isStudioLink = (url: unknown) => typeof url === 'string' && STUDIO.test(url.trim());

/**
 * A picture field given a YouTube link (Studio or public): the video's
 * thumbnail, which is a picture. Anything else unchanged.
 */
export function normalizeImageLink<T extends string | null | undefined>(url: T): T {
  if (typeof url !== 'string') return url;
  const trimmed = url.trim();
  if (!/youtu\.?be|youtube\.com/i.test(trimmed)) return trimmed as T;
  const id = trimmed.match(YOUTUBE_ID)?.[1];
  return (id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : trimmed) as T;
}

/** The video and picture fields of a course or lesson record, cleaned before saving. */
export function normalizeMediaFields<T extends Record<string, unknown>>(data: T): T {
  const out: Record<string, unknown> = { ...data };
  for (const k of ['videoUrl', 'fullVideoUrl', 'previewVideoUrl']) {
    if (typeof out[k] === 'string') out[k] = normalizeVideoLink(out[k] as string);
  }
  for (const k of ['imageUrl', 'thumbnailUrl']) {
    if (typeof out[k] === 'string') out[k] = normalizeImageLink(out[k] as string);
  }
  return out as T;
}
